/**
 * TokensSection.js — Section 3: Token configuration list.
 *
 * Each mechanic can define custom tokens (e.g., "Crystal Shards", "Wither Marks").
 * Each token has an id, name, max value (number or $ref expression), and refresh trigger.
 *
 * Part of the Homebrew Workshop (Issue #82, Phase E: #180)
 */

import React from 'react';
import { Trash2 } from 'lucide-react';
import ExpressionInput from '../forms/ExpressionInput';

// Simplified refresh options for homebrew UI (schema keeps full set for SRD compat)
const REFRESH_OPTIONS = [
  { value: 'session', label: 'New Session' },
  { value: 'long-rest', label: 'Downtime' },
];

export default function TokensSection({ config, dispatch, errors }) {
  const tokens = config.tokens || [];

  return (
    <div className="space-y-3">
      {tokens.map((token, i) => (
        <div key={i} className="p-3 bg-gray-800/50 border border-gray-700 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="flex-1 grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-400">Name</label>
                <input
                  value={token.name || ''}
                  onChange={e => {
                    const name = e.target.value;
                    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                    dispatch({ type: 'UPDATE_TOKEN', tokenIndex: i, updates: { name, id: slug || token.id } });
                  }}
                  placeholder="e.g. Crystal Shards"
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-1.5 text-sm text-gray-100 focus:border-red-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-400">Max</label>
                <ExpressionInput
                  value={token.max}
                  onChange={val => dispatch({ type: 'UPDATE_TOKEN', tokenIndex: i, updates: { max: val } })}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-400">Refresh On</label>
                <select
                  value={token.refreshOn || 'session'}
                  onChange={e => dispatch({ type: 'UPDATE_TOKEN', tokenIndex: i, updates: { refreshOn: e.target.value } })}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-1.5 text-sm text-gray-100 focus:border-red-500 focus:outline-none"
                >
                  {REFRESH_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              {/* ID auto-generated from name — no need to show it */}
            </div>
            <button
              type="button"
              onClick={() => dispatch({ type: 'REMOVE_TOKEN', tokenIndex: i })}
              className="mt-5 text-gray-500 hover:text-red-400"
              title="Remove token"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}

      {tokens.length === 0 && (
        <p className="text-xs text-gray-500 text-center py-1">Tokens are trackable resources like charges, marks, or stacks.</p>
      )}
      <button
        type="button"
        onClick={() => dispatch({ type: 'ADD_TOKEN' })}
        className="w-full py-2 border border-dashed border-gray-600 rounded text-sm text-gray-400 hover:text-red-400 hover:border-red-700"
      >
        + Add Token
      </button>
    </div>
  );
}
